import { ItemType, isAppHttpError } from '@inno-sdks/files'
import { HttpStatusCode } from 'axios'
import { logger } from 'shared/services/logger'

import type { FilesApp } from '../api/filesSdk'

type ProvisionDesktopShortcutOptions = {
  filesSdk: FilesApp
  appFolderId: number
  shortcutKey: string
  name: string
  shortcutUrl: string
  failureEvent: string
}

export type ProvisionDesktopShortcutResult =
  | 'created'
  | 'alreadyExists'
  | 'failed'

const ALREADY_EXISTS_MESSAGE = /already\s+exists|duplicate/i

/** Recognizes only an explicit duplicate response, not every validation error. */
const isAlreadyExistsError = (error: unknown): boolean =>
  isAppHttpError(error) &&
  (error.statusCode === HttpStatusCode.BadRequest ||
    error.statusCode === HttpStatusCode.Conflict) &&
  ALREADY_EXISTS_MESSAGE.test(error.message)

/**
 * Creates one permanent desktop shortcut and distinguishes a confirmed
 * duplicate from both a new creation and a genuine failure.
 */
export async function provisionDesktopShortcut({
  filesSdk,
  appFolderId,
  shortcutKey,
  name,
  shortcutUrl,
  failureEvent
}: ProvisionDesktopShortcutOptions): Promise<ProvisionDesktopShortcutResult> {
  try {
    await filesSdk.items.create(
      [
        {
          type: ItemType.Shortcut,
          name,
          shortcutUrl,
          extra: { isPermanent: true }
        }
      ],
      appFolderId
    )
    return 'created'
  } catch (error) {
    if (isAlreadyExistsError(error)) {
      return 'alreadyExists'
    }

    logger.exception(
      failureEvent,
      error,
      {
        shortcut: shortcutKey,
        appFolderId,
        statusCode: isAppHttpError(error) ? error.statusCode : undefined
      },
      { category: 'business' }
    )
    return 'failed'
  }
}
